import React, { useEffect, useRef } from 'react';

/**
 * THE RIGHT-CLICK MENU — the second convention every canvas tool shares.
 *
 * ComfyUI, Miro and Figma all put "what can I do here?" behind a right-click, and creators coming from any of
 * them will try it first. On empty canvas it offers the canvas verbs (add, fit, actual size); on a card it
 * offers that card's verbs. Same menu, contents chosen by what was under the cursor — which is exactly how the
 * tools above behave, and why it needs no explaining.
 *
 * Presentational only: items are supplied by the board, so this file knows nothing about shots or services.
 */
export interface MenuItem {
  label: string;
  onSelect: () => void;
  /** Right-aligned shortcut hint, e.g. "⇧1". Purely informational. */
  hint?: string;
  disabled?: boolean;
  /** Draw a separator ABOVE this item. */
  divider?: boolean;
}

export interface BoardContextMenuProps {
  x: number;
  y: number;
  items: MenuItem[];
  onClose: () => void;
}

const BoardContextMenu: React.FC<BoardContextMenuProps> = ({ x, y, items, onClose }) => {
  const ref = useRef<HTMLDivElement>(null);

  // Dismiss the way every native menu does: a click anywhere else, Escape, or the canvas moving under it.
  useEffect(() => {
    const away = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) onClose(); };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    // `capture` so we still close when the click lands on the canvas, which stops propagation of its own.
    document.addEventListener('mousedown', away, true);
    document.addEventListener('keydown', key);
    window.addEventListener('resize', onClose);
    return () => {
      document.removeEventListener('mousedown', away, true);
      document.removeEventListener('keydown', key);
      window.removeEventListener('resize', onClose);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      data-testid="board-context-menu"
      role="menu"
      // Nudged off the cursor so the pointer is not already resting on the first item — the same one-pixel
      // courtesy native menus do, which stops an accidental double-click firing an action.
      style={{ left: x + 2, top: y + 2 }}
      className="absolute z-40 min-w-[190px] rounded-lg border border-secondary-200 bg-white/97 p-1 shadow-2xl backdrop-blur
                 dark:border-secondary-700 dark:bg-secondary-900/97"
      onPointerDown={(e) => e.stopPropagation()}
    >
      {items.map((it, i) => (
        <React.Fragment key={it.label}>
          {it.divider && i > 0 && <div className="my-1 h-px bg-secondary-200 dark:bg-secondary-800" aria-hidden />}
          <button
            type="button"
            role="menuitem"
            disabled={it.disabled}
            onClick={() => { it.onSelect(); onClose(); }}
            className="flex w-full items-center justify-between gap-6 rounded px-2.5 py-1.5 text-left text-xs
                       text-secondary-700 transition-colors hover:bg-secondary-100 hover:text-secondary-900
                       dark:text-secondary-200 dark:hover:bg-secondary-800 dark:hover:text-white
                       disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
          >
            {it.label}
            {it.hint && <span className="font-mono text-[11px] text-secondary-500">{it.hint}</span>}
          </button>
        </React.Fragment>
      ))}
    </div>
  );
};

export default BoardContextMenu;
